import type {
  BookingBucket,
  EffectiveAvailability,
  PriceSource,
  RateGridBookingSummary,
  RateGridDay,
  RateGridOccupancy,
  RateGridOccupancyBooking,
  RateGridRestrictionValues,
  RateGridTarget,
  TargetStatus,
} from '@interfaces/rate-grid.interface';

type WireTarget = Omit<RateGridTarget, 'failure'> & {
  failure: {
    code: string;
    correlationId: string;
    details: { target: string; retryAllowed: boolean };
  } | null;
};

export type MockDay = Omit<
  RateGridDay,
  'isCheckout' | 'isStayStart' | 'isStayEnd' | 'isReadEnd' | 'handedOverBy' | 'targets'
> & {
  targets: WireTarget[];
};

export const CURRENCY = 'EUR';

export const TIME_ZONE = 'Europe/Madrid';

export const BASE_PRICE = 300;

export const SEASON_PRICE = 250;

export const TARGET_WEB = 'WEB_PUBLICA';

export const FREE_OCCUPANCY: RateGridOccupancy = {
  status: 'FREE',
  bookingId: null,
  reference: null,
  guestDisplayName: null,
  bucket: null,
  channel: null,
};

export const NO_OVERRIDES: RateGridRestrictionValues = {
  minStay: null,
  maxStay: null,
  cta: null,
  ctd: null,
};

export function occupancyOf(
  bookingId: string,
  reference: string,
  guestDisplayName: string,
  bucket: BookingBucket,
  channel: string | null = null,
  booking: RateGridOccupancyBooking | null = null
): RateGridOccupancy {
  return { status: 'OCCUPIED', bookingId, reference, guestDisplayName, bucket, channel, booking };
}

type BookingStates = Pick<RateGridOccupancyBooking, 'bookingStatus' | 'paymentState' | 'mirState'>;

/**
 * The summary the grid read embeds in each night of a stay (`occupancy.booking`): the standalone
 * summary plus the three workflow states and the automatic cancellations captured on confirmation.
 */
export function embeddedBookingOf(
  summary: RateGridBookingSummary,
  states: Partial<BookingStates> = {}
): RateGridOccupancyBooking {
  const bookingStatus = states.bookingStatus ?? summary.substate ?? 'CHECKIN_SCHEDULED';
  return {
    ...summary,
    substate: bookingStatus,
    bookingStatus,
    paymentState: states.paymentState ?? 'PAYMENT_PAID',
    mirState: states.mirState ?? 'MIR_COMPLETED',
    navigationPath: summary.navigationPath ?? `/booking/${summary.bookingId}/settings`,
    paymentTimeoutEnabled: true,
    mirTimeoutEnabled: true,
    configurationCaptured: true,
  };
}

export function targetAt(
  target: string,
  status: TargetStatus,
  updatedAt: string | null,
  operationId: string | null = null,
  retryAllowed = false
): WireTarget {
  return {
    target,
    status,
    updatedAt,
    correlationId: null,
    operationId,
    retryAllowed,
    // The wire shape: `retryAllowed` lives inside `details`, not beside the code.
    failure:
      status === 'FAILED'
        ? {
            code: 'RATE_GRID_WEB_PUBLICATION_FAILED',
            correlationId: 'mock-correlation-id',
            details: { target, retryAllowed },
          }
        : null,
  };
}

/** Builds an ordinary, unoccupied night: priced, open, published, with the house stay rules. */
export function ordinaryDay(date: string, amount: number, source: PriceSource = 'BASE'): MockDay {
  return {
    date,
    temporalState: 'FUTURE',
    version: 1,
    price: { amount, currency: CURRENCY, source },
    occupancy: { ...FREE_OCCUPANCY },
    manualClosure: { closed: false, reason: null },
    effectiveAvailability: 'AVAILABLE',
    season: null,
    restrictions: {
      overrides: { ...NO_OVERRIDES },
      effective: { minStay: 2, maxStay: 14, cta: false, ctd: false },
    },
    targets: [targetAt(TARGET_WEB, 'PUBLISHED', `${date}T00:00:00Z`)],
    lastChange: null,
  };
}

/**
 * What the night is actually doing, in the server's precedence: a booking outranks a closure, and a
 * night with no price cannot be sold whatever else is true of it.
 */
export function availabilityOf(day: MockDay): EffectiveAvailability {
  if (day.occupancy.status === 'OCCUPIED') {
    return 'OCCUPIED';
  }
  if (day.manualClosure.closed) {
    return 'MANUALLY_CLOSED';
  }
  return day.price.amount === null ? 'NO_PRICE' : 'AVAILABLE';
}

/** Stamps the two fields the server resolves per read rather than storing. */
export function resolveDay(day: MockDay, today: string): MockDay {
  return {
    ...day,
    temporalState: day.date < today ? 'PAST' : day.date === today ? 'TODAY' : 'FUTURE',
    effectiveAvailability: availabilityOf(day),
  };
}

/** Sets a rule as an exception, keeping `effective` in step the way inheritance would. */
export function withRestrictions(
  day: MockDay,
  overrides: Partial<RateGridRestrictionValues>
): MockDay {
  const nextOverrides = { ...day.restrictions.overrides, ...overrides };
  return {
    ...day,
    restrictions: {
      overrides: nextOverrides,
      effective: {
        minStay: nextOverrides.minStay ?? day.restrictions.effective.minStay,
        maxStay: nextOverrides.maxStay ?? day.restrictions.effective.maxStay,
        cta: nextOverrides.cta ?? day.restrictions.effective.cta,
        ctd: nextOverrides.ctd ?? day.restrictions.effective.ctd,
      },
    },
  };
}

/** A refusal the interceptor turns into an HTTP status and `{code, message}` body. */
export class RateGridMockError extends Error {
  constructor(
    readonly status: number,
    readonly code: string
  ) {
    super(code);
    this.name = 'RateGridMockError';
  }
}
